<template>
    <PanelTransition :name="transitionName">
        <div
            v-if="ui.notesModal.opened"
            class="fixed inset-0 z-[60]"
            :class="onlyMobileVisible ? 'flex flex-col justify-end' : ''"
            @click.stop="closeClickingOutsideElement($event)"
        >
            <div
                ref="focusTrapContainer"
                role="dialog"
                aria-modal="true"
                :aria-label="localizations[lang].note"
                class="w-full bg-theme-bg shadow-lg"
                :class="panelClasses"
                :style="panelStyle"
            >
                <!-- Drag pill for mobile bottom sheet -->
                <button
                    v-if="onlyMobileVisible"
                    :aria-label="localizations[lang].close"
                    class="flex w-full shrink-0 cursor-pointer items-center justify-center border-none bg-transparent pb-0.5 pt-2.5"
                    @click="closeModal"
                >
                    <span class="h-1 w-9 rounded-sm bg-theme-toolbar-icon opacity-40" />
                </button>

                <div class="p-1 pt-3">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center">
                            <svg-icon icon="note" class="mr-2 h-6 w-6 text-theme-icon"></svg-icon>
                            <h2 class="font-noto-sans my-auto text-left font-medium leading-6 text-theme-text">
                                {{ localizations[lang].note }}
                            </h2>
                        </div>
                        <BaseButton
                            v-if="!onlyMobileVisible"
                            icon="ic_close_white_24px"
                            width="24px"
                            height="24px"
                            :tooltip="localizations[lang].close"
                            class="text-theme-toolbar-icon hover:text-theme-text"
                            @click="closeModal"
                        />
                        <button
                            v-else
                            dusk="SaveNote"
                            class="mr-2 flex items-center text-theme-accent hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            :disabled="!canSaveOrUpdate"
                            @click="applyColorAndConfirmNote"
                        >
                            {{ localizations[lang].save }}
                        </button>
                    </div>
                </div>
                <div class="min-h-0 flex-1 overflow-y-auto">
                    <div class="p-4">
                        <div class="flex">
                            <div class="relative mr-2 w-1">
                                <div
                                    class="absolute inset-y-0 left-0 w-full"
                                    :style="{ backgroundColor: selectedColor }"
                                ></div>
                            </div>
                            <div class="flex-1">
                                <div
                                    ref="selectedText"
                                    role="button"
                                    tabindex="0"
                                    class="font-noto-sans cursor-pointer rounded text-left font-normal leading-6 text-theme-text focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                                    :title="localizations[lang].clickToToggle"
                                    @click="toggleTextExpansion"
                                >
                                    {{
                                        isExpanded || currentHighlight.totalText?.length <= 45
                                            ? currentHighlight.totalText
                                            : currentHighlight.totalText.slice(
                                                  0,
                                                  currentHighlight.totalText.lastIndexOf(' ', 45)
                                              ) + ' [...]'
                                    }}
                                </div>
                                <p v-if="isPdf" class="mb-1 text-xs text-theme-toolbar-icon">
                                    {{ localizations[lang].note }} - {{ localizations[lang].page }}
                                    {{ ui.selectionTool.page }}
                                </p>
                                <div class="h-[25px]"></div>
                                <div class="relative">
                                    <pre
                                        ref="pre"
                                        class="font-noto-sans invisible overflow-hidden whitespace-pre-wrap break-words text-sm leading-5"
                                        >{{ annotation + '\n' }}</pre
                                    >
                                    <textarea
                                        ref="noteTextarea"
                                        v-model="annotation"
                                        v-focus
                                        dusk="noteTextarea"
                                        :placeholder="localizations[lang].writeNote"
                                        class="font-noto-sans absolute left-0 top-0 h-full w-full resize-none overflow-hidden border-none bg-transparent text-left text-sm font-normal leading-5 text-theme-text placeholder-theme-toolbar-icon outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                                        @input="onTextareaInput"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="p-2">
                    <div class="mb-4 flex items-center justify-between">
                        <div class="flex space-x-2">
                            <div
                                v-for="(color, index) in defaultColors"
                                :key="index + '-modal'"
                                v-tooltip.top-start="color.lang"
                                class="relative"
                            >
                                <input
                                    :id="'filterAnnotationColor-' + color.lang"
                                    v-model="selectedColor"
                                    :value="color.code"
                                    type="radio"
                                    class="hidden"
                                />
                                <label
                                    :for="'filterAnnotationColor-' + color.lang"
                                    class="relative block h-6 w-6 cursor-pointer rounded-full border border-theme-border transition-all duration-200"
                                    :class="[selectedColor === color.code ? 'border-2 border-theme-text' : '']"
                                >
                                    <span
                                        class="absolute inset-0 rounded-full transition-all duration-200"
                                        :style="{ backgroundColor: color.code }"
                                    ></span>
                                    <span
                                        v-if="selectedColor === color.code"
                                        class="absolute inset-0 flex items-center justify-center"
                                    >
                                        <span class="h-2 w-2 rounded-full bg-theme-bg"></span>
                                    </span>
                                </label>
                            </div>
                        </div>
                        <ComponentSpeechToText
                            v-if="showSpeechToText"
                            :text="annotation"
                            :speech-to-text-lang="setSpeechToTextLang"
                            @update:text="annotation = $event"
                        />
                    </div>
                    <div v-if="!onlyMobileVisible" class="flex w-full space-x-2">
                        <button
                            v-if="needUpdated"
                            dusk="DeleteNote"
                            class="w-1/2 rounded-lg border border-theme-border bg-theme-bg py-2 text-theme-text hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            @click="deleteAnnotation"
                        >
                            {{ localizations[lang].delete }}
                        </button>
                        <button
                            v-else
                            class="w-1/2 rounded-lg border border-theme-border bg-theme-bg py-2 text-theme-text hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            @click="closeNotesModal"
                        >
                            {{ localizations[lang].cancel }}
                        </button>
                        <button
                            dusk="SaveNote"
                            class="w-1/2 rounded-lg bg-theme-accent py-2 text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            :disabled="!canSaveOrUpdate"
                            @click="applyColorAndConfirmNote"
                        >
                            {{ localizations[lang].save }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </PanelTransition>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import ComponentSpeechToText from './ComponentSpeechToText.vue';
import BaseButton from '../Components/buttons/BaseButton.vue';
import PanelTransition from '../Components/PanelTransition.vue';
import focusTrapMixin from '../../js/mixins/focusTrapMixin';

export default {
    components: {
        ComponentSpeechToText,
        BaseButton,
        PanelTransition,
    },
    directives: {
        focus: {
            inserted(el) {
                el.focus();
            },
        },
    },
    mixins: [focusTrapMixin],
    data() {
        return {
            annotation: '',
            selectedColor: '',
            isExpanded: false,
            focusTrapEnabled: false,
        };
    },
    computed: {
        ...mapState([
            'ui',
            'localizations',
            'lang',
            'selections',
            'defaultColors',
            'currentHighlight',
            'isChrome',
            'isSafari',
            'device',
        ]),
        ...mapGetters(['onlyMobileVisible', 'isPdf', 'isDesktopBrowser']),
        canSaveOrUpdate() {
            return this.annotation.length > 0 && this.selectedColor.length > 0;
        },
        needUpdated() {
            if (Object.keys(this.currentHighlight).length > 0) {
                return this.currentHighlight.hasAnnotation;
            } else {
                return false;
            }
        },
        setSpeechToTextLang() {
            const speechLang = {
                es: 'es-AR',
                en: 'en-US',
                pt: 'pt-BR',
            };

            return speechLang[this.lang];
        },
        showSpeechToText() {
            return this.isDesktopBrowser;
        },
        panelSide() {
            return this.ui.selectionTool.panelSide || 'right';
        },
        panelStyle() {
            if (this.onlyMobileVisible) {
                return {};
            }

            const viewportHeight = this.device.height || window.innerHeight;
            const selectionTop = this.ui.selectionTool.selectionTop || 0;
            const contentRight = this.ui.selectionTool.contentRight || 0;
            const contentLeft = this.ui.selectionTool.contentLeft || 0;

            const panelWidth = 320;
            const panelMaxHeight = viewportHeight * 0.5;
            const bottomMargin = 24;
            const viewportMiddle = viewportHeight / 2;

            const style = {
                position: 'fixed',
                width: `${panelWidth}px`,
                maxHeight: '50vh',
                borderRadius: '12px',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
            };

            const isSelectionBelowMiddle = selectionTop > viewportMiddle;
            const remainingSpaceBelowSelection = viewportHeight - selectionTop;
            const shouldRepositionToBottom = isSelectionBelowMiddle && remainingSpaceBelowSelection < panelMaxHeight;

            if (shouldRepositionToBottom) {
                style.bottom = `${bottomMargin}px`;
            } else {
                const top = Math.max(80, Math.min(selectionTop, viewportHeight - panelMaxHeight));
                style.top = `${top}px`;
            }

            if (this.panelSide === 'right') {
                style.right = `${Math.max(4, contentRight - panelWidth - 16)}px`;
            } else {
                style.left = `${Math.max(4, contentLeft - panelWidth - 16)}px`;
            }

            return style;
        },
        panelClasses() {
            if (this.onlyMobileVisible) {
                return 'max-w-full max-h-[85vh] overflow-y-auto flex flex-col rounded-t-xl shadow-[0_-4px_24px_rgba(0,0,0,0.15)]';
            }
            return 'overflow-y-auto max-w-md flex flex-col md:max-h-[50vh]';
        },
        transitionName() {
            if (this.onlyMobileVisible) return 'slide-up';
            return this.panelSide === 'right' ? 'slide-right' : 'slide-left';
        },
    },
    watch: {
        'ui.notesModal.opened': {
            immediate: true,
            handler(newVal) {
                this.focusTrapEnabled = newVal;
                if (newVal) {
                    this.pauseKeyboardNavigation();
                    this.focusTextarea();
                    this.isExpanded = false;
                } else {
                    this.resumeKeyboardNavigation();
                }
            },
        },
        currentHighlight: {
            handler(currentHighlight) {
                this.selectedColor = currentHighlight.color || this.selectedColor;

                if (this.ui.notesModal.opened) {
                    this.annotation = currentHighlight.annotationText || '';
                    this.$nextTick(() => {
                        this.adjustTextareaHeight();
                    });
                } else {
                    this.annotation = '';
                }
            },
        },
    },
    created() {
        this.setColor();
    },
    mounted() {
        this.adjustTextareaHeight();
        this.focusTextarea();
    },
    methods: {
        ...mapMutations([
            'closeNotesModal',
            'setCurrentHighlight',
            'hideSelectionTool',
            'pauseKeyboardNavigation',
            'resumeKeyboardNavigation',
        ]),
        onFocusTrapEscape() {
            this.closeModal();
        },
        focusTextarea() {
            this.$nextTick(() => {
                if (this.$refs.noteTextarea) {
                    this.$refs.noteTextarea.focus();
                }
            });
        },
        toggleTextExpansion() {
            this.isExpanded = !this.isExpanded;
        },
        applyColorAndConfirmNote() {
            if (this.canSaveOrUpdate) {
                this.$bus.$emit('applyColorAndConfirmSelection', {
                    color: this.selectedColor,
                    page: this.ui.selectionTool.page,
                    annotationText: this.annotation,
                    hasAnnotation: true,
                    needUpdated: this.needUpdated,
                });
            }
            this.closeModal();
        },
        deleteAnnotation() {
            this.closeModal();
            if (!this.anonymousSession) {
                this.currentHighlight.destroy();
            }
            this.$store.commit('removeSelection', {
                uuid: this.currentHighlight.uuid,
            });
            this.$bus.$emit('selectionDestroyed', this.currentHighlight.getUniqueCssClass());
            this.setCurrentHighlight({});
            this.hideSelectionTool();
        },
        closeClickingOutsideElement(event) {
            if (this.$el === event.target) {
                this.closeModal();
            }
        },
        closeModal() {
            this.focusTrapEnabled = false;
            this.annotation = '';
            this.isExpanded = false;
            this.closeNotesModal();
        },
        setColor() {
            this.selectedColor = this.defaultColors[0].code;
        },

        adjustTextareaHeight() {
            const pre = this.$refs.pre;
            const textarea = this.$refs.noteTextarea;
            if (pre && textarea) {
                pre.textContent = `${this.annotation}\n`;
                textarea.style.height = `${pre.offsetHeight}px`;
            }
        },
        onTextareaInput() {
            const textarea = this.$refs.noteTextarea;
            const pre = this.$refs.pre;

            if (pre && textarea) {
                pre.textContent = `${this.annotation}\n`;
                textarea.style.height = 'auto';
                textarea.style.height = `${pre.offsetHeight}px`;
            }
        },
    },
};
</script>
